import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "searchInput", "frame", "viewBtn",
    "statusCheckbox", "statusToggleAll",
    "regionCheckbox", "regionToggleAll",
    "dateFrom", "dateTo", "datePresets", "exportButton",
    "filterBadge", "clearFiltersRow",
    "keyDatesModal", "keyDatesCheckbox"
  ]
  static values = {
    viewMode: { type: String, default: "thin" },
    savedSort: { type: String, default: "name" },
    savedRegions: { type: Array, default: [] },
    savedDateFrom: { type: String, default: "" },
    savedDateTo: { type: String, default: "" },
    savedDatePreset: { type: String, default: "" }
  }

  connect() {
    this._debounceTimer = null
    this._currentSort = this.savedSortValue || "name"
    this._currentDatePreset = this.savedDatePresetValue || ""

    // Restore saved date range
    if (this._currentDatePreset) {
      this._applyPresetDates(this._currentDatePreset)
    } else if (this.savedDateFromValue || this.savedDateToValue) {
      if (this.savedDateFromValue && this.hasDateFromTarget) this.dateFromTarget.value = this.savedDateFromValue
      if (this.savedDateToValue && this.hasDateToTarget) this.dateToTarget.value = this.savedDateToValue
    }

    this._updateFilterBadges()
    this._updateDatePresetChips()
  }

  // Debounced search
  search() {
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this._reload(), 300)
  }

  // Status filter: individual checkbox change
  filterStatuses() {
    const checkboxes = this.statusCheckboxTargets
    const allChecked = checkboxes.every((cb) => cb.checked)
    this.statusToggleAllTargets.forEach((toggle) => {
      toggle.checked = allChecked
      toggle.indeterminate = !allChecked && checkboxes.some((cb) => cb.checked)
    })

    this._reload()
  }

  // Status filter: toggle all checkboxes
  toggleAllStatuses(event) {
    const checked = event.currentTarget.checked
    this.statusCheckboxTargets.forEach((cb) => { cb.checked = checked })
    this.statusToggleAllTargets.forEach((toggle) => {
      toggle.checked = checked
      toggle.indeterminate = false
    })
    this._reload()
  }

  // Sort from dropdown
  sort(event) {
    this._currentSort = event.currentTarget.value
    this._reload()
  }

  // View mode toggle (card / thin)
  toggleView(event) {
    const mode = event.currentTarget.dataset.viewMode
    this.viewModeValue = mode

    this.viewBtnTargets.forEach((b) => {
      if (b.dataset.viewMode === mode) {
        b.classList.add("text-brand", "font-bold")
        b.classList.remove("text-text-muted", "font-medium", "hover:text-gray-700")
      } else {
        b.classList.remove("text-brand", "font-bold")
        b.classList.add("text-text-muted", "font-medium", "hover:text-gray-700")
      }
    })

    // Save preference
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(`/design/save_design_view_mode?mode=${mode}`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken }
    })

    this._reload()
  }

  // Region filter: individual checkbox change
  filterRegions() {
    const checkboxes = this.regionCheckboxTargets
    const allChecked = checkboxes.every((cb) => cb.checked)
    this.regionToggleAllTargets.forEach((toggle) => {
      toggle.checked = allChecked
      toggle.indeterminate = !allChecked && checkboxes.some((cb) => cb.checked)
    })

    this._reload()
  }

  // Region filter: toggle all checkboxes
  toggleAllRegions(event) {
    const checked = event.currentTarget.checked
    this.regionCheckboxTargets.forEach((cb) => { cb.checked = checked })
    this.regionToggleAllTargets.forEach((toggle) => {
      toggle.checked = checked
      toggle.indeterminate = false
    })
    this._reload()
  }

  // Date preset selection
  selectDatePreset(event) {
    const preset = event.currentTarget.dataset.preset
    this._currentDatePreset = preset

    this._applyPresetDates(preset)
    this._updateDatePresetChips()
    this._reload()
  }

  // Custom date range filter
  filterDates() {
    this._currentDatePreset = ""
    this._updateDatePresetChips()
    this._reload()
  }

  // Clear all filters
  clearFilters() {
    // Reset statuses to all checked
    this.statusCheckboxTargets.forEach((cb) => { cb.checked = true })
    this.statusToggleAllTargets.forEach((toggle) => { toggle.checked = true; toggle.indeterminate = false })

    // Reset regions to all checked
    this.regionCheckboxTargets.forEach((cb) => { cb.checked = true })
    this.regionToggleAllTargets.forEach((toggle) => { toggle.checked = true; toggle.indeterminate = false })

    // Reset date to "All Dates"
    this._currentDatePreset = ""
    if (this.hasDateFromTarget) this.dateFromTarget.value = ""
    if (this.hasDateToTarget) this.dateToTarget.value = ""
    this._updateDatePresetChips()

    this._reload()
  }

  _applyPresetDates(preset) {
    const today = new Date()
    let from = ""
    let to = ""

    if (preset === "30" || preset === "90" || preset === "365") {
      const days = parseInt(preset)
      const fromDate = new Date(today)
      fromDate.setDate(fromDate.getDate() - days)
      from = this._formatDate(fromDate)
      to = this._formatDate(today)
    } else if (preset === "ytd") {
      from = `${today.getFullYear()}-01-01`
      to = this._formatDate(today)
    }

    if (this.hasDateFromTarget) this.dateFromTarget.value = from
    if (this.hasDateToTarget) this.dateToTarget.value = to
  }

  _formatDate(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  // Export: Key Dates PDF — open modal
  exportKeyDatesReport() {
    const dropdown = this.element.querySelector('[data-controller="dropdown"] [data-dropdown-target="menu"]')
    if (dropdown) dropdown.classList.add("hidden")

    if (this.hasKeyDatesModalTarget) {
      this.keyDatesModalTarget.classList.remove("hidden")
    }
  }

  // Close key dates modal
  closeKeyDatesModal() {
    if (this.hasKeyDatesModalTarget) {
      this.keyDatesModalTarget.classList.add("hidden")
    }
  }

  // Toggle all key dates checkboxes
  toggleAllKeyDates(event) {
    const checked = event.currentTarget.checked
    this.keyDatesCheckboxTargets.forEach((cb) => { cb.checked = checked })
  }

  // Confirm key dates export — save IDs then open PDF
  confirmKeyDatesExport() {
    const selected = this.keyDatesCheckboxTargets
      .filter((cb) => cb.checked)
      .map((cb) => cb.value)

    if (selected.length === 0) return

    this._showExportSpinner()

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch("/design/set_kd_pdf_projects", {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ project_ids: JSON.stringify(selected) })
    })
      .then((response) => {
        if (response.ok) {
          window.open("/design/key_dates_by_projects_export.pdf", "_blank")
          this._showToast("Key Dates report exported successfully")
        } else {
          throw new Error("Export failed")
        }
      })
      .catch(() => this._showToast("Failed to export. Please try again.", "error"))
      .finally(() => {
        this._hideExportSpinner()
        this.closeKeyDatesModal()
      })
  }

  // Export: Management Report (Excel)
  exportManagementReport() {
    const dropdown = this.element.querySelector('[data-controller="dropdown"] [data-dropdown-target="menu"]')
    if (dropdown) dropdown.classList.add("hidden")

    this._showExportSpinner()

    const params = new URLSearchParams()

    const q = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ""
    if (q) params.set("q", q)

    if (this.hasStatusCheckboxTarget) {
      const selected = this.statusCheckboxTargets
        .filter((cb) => cb.checked)
        .map((cb) => cb.value)
      const total = this.statusCheckboxTargets.length
      if (selected.length < total) {
        selected.forEach((f) => params.append("filter[]", f))
      }
    }

    params.set("sort", this._currentSort || "name")

    if (this.hasRegionCheckboxTarget) {
      const unique = new Set(this.regionCheckboxTargets.map((cb) => cb.value))
      const selected = [...new Set(
        this.regionCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
      )]
      if (selected.length < unique.size) {
        selected.forEach((r) => params.append("regions[]", r))
      }
    }

    if (this.hasDateFromTarget && this.dateFromTarget.value) {
      params.set("date_from", this.dateFromTarget.value)
    }
    if (this.hasDateToTarget && this.dateToTarget.value) {
      params.set("date_to", this.dateToTarget.value)
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch("/design/export_management_report", {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body: params.toString()
    })
      .then((response) => {
        if (!response.ok) throw new Error("Export failed")
        return response.blob()
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        const today = new Date().toISOString().split("T")[0]
        a.download = `management_report_${today}.xlsx`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
        this._showToast("Management report exported successfully")
      })
      .catch(() => this._showToast("Failed to export. Please try again.", "error"))
      .finally(() => this._hideExportSpinner())
  }

  _showExportSpinner() {
    if (!this.hasExportButtonTarget) return
    const btn = this.exportButtonTarget
    btn._originalHTML = btn.innerHTML
    btn.style.width = `${btn.offsetWidth}px`
    btn.classList.add("justify-center")
    btn.disabled = true
    btn.innerHTML = `<svg class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`
  }

  _hideExportSpinner() {
    if (!this.hasExportButtonTarget) return
    const btn = this.exportButtonTarget
    if (btn._originalHTML) {
      btn.innerHTML = btn._originalHTML
      delete btn._originalHTML
    }
    btn.style.width = ""
    btn.classList.remove("justify-center")
    btn.disabled = false
  }

  _reload() {
    this._updateFilterBadges()
    const frame = this.frameTarget
    frame.src = this._buildUrl()
  }

  _buildUrl() {
    const endpoint = `/design/list_projects`

    const url = new URL(endpoint, window.location.origin)
    url.searchParams.set("page", "1")
    url.searchParams.set("format", "html")

    const q = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ""
    if (q) url.searchParams.set("q", q)

    // Status filter params
    if (this.hasStatusCheckboxTarget) {
      const selected = this.statusCheckboxTargets
        .filter((cb) => cb.checked)
        .map((cb) => cb.value)
      const total = this.statusCheckboxTargets.length

      if (selected.length < total) {
        selected.forEach((f) => url.searchParams.append("filter[]", f))
      }
    }

    url.searchParams.set("sort", this._currentSort || "name")
    url.searchParams.set("view_mode", this.viewModeValue)

    // Region filter params
    if (this.hasRegionCheckboxTarget) {
      const selected = this.regionCheckboxTargets
        .filter((cb) => cb.checked)
        .map((cb) => cb.value)
      const total = this.regionCheckboxTargets.length

      if (selected.length < total) {
        selected.forEach((r) => url.searchParams.append("regions[]", r))
      }
    }

    // Date filter params
    if (this.hasDateFromTarget && this.dateFromTarget.value) {
      url.searchParams.set("date_from", this.dateFromTarget.value)
    }
    if (this.hasDateToTarget && this.dateToTarget.value) {
      url.searchParams.set("date_to", this.dateToTarget.value)
    }
    if (this._currentDatePreset) {
      url.searchParams.set("date_preset", this._currentDatePreset)
    }

    return url.toString()
  }

  _updateFilterBadges() {
    let count = 0

    // Count active status filters
    if (this.statusCheckboxTargets.length) {
      const selected = this.statusCheckboxTargets.filter((cb) => cb.checked).length
      if (selected < this.statusCheckboxTargets.length) count++
    }

    // Count active region filters
    if (this.regionCheckboxTargets.length) {
      const selected = this.regionCheckboxTargets.filter((cb) => cb.checked).length
      if (selected < this.regionCheckboxTargets.length) count++
    }

    // Count active date filter
    const hasDateFrom = this.hasDateFromTarget && this.dateFromTarget.value
    const hasDateTo = this.hasDateToTarget && this.dateToTarget.value
    if (hasDateFrom || hasDateTo || this._currentDatePreset) count++

    // Update badge
    if (this.hasFilterBadgeTarget) {
      if (count > 0) {
        this.filterBadgeTarget.textContent = count
        this.filterBadgeTarget.classList.remove("hidden")
        this.filterBadgeTarget.classList.add("inline-flex")
      } else {
        this.filterBadgeTarget.classList.add("hidden")
        this.filterBadgeTarget.classList.remove("inline-flex")
      }
    }

    // Show/hide clear row
    if (this.hasClearFiltersRowTarget) {
      if (count > 0) {
        this.clearFiltersRowTarget.classList.remove("hidden")
      } else {
        this.clearFiltersRowTarget.classList.add("hidden")
      }
    }
  }

  _updateDatePresetChips() {
    if (!this.hasDatePresetsTarget) return
    const buttons = this.datePresetsTarget.querySelectorAll("button")
    buttons.forEach((btn) => {
      const preset = btn.dataset.preset
      const isActive = preset === (this._currentDatePreset || "")
      if (isActive) {
        btn.className = "px-2.5 py-1 text-[13px] rounded-full transition-colors cursor-pointer border bg-brand/10 text-brand font-medium border-brand/30"
      } else {
        btn.className = "px-2.5 py-1 text-[13px] rounded-full transition-colors cursor-pointer border text-gray-500 hover:bg-gray-50 border-gray-200"
      }
    })
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
